import { useCallback, useMemo, useState } from 'react';

import { formatMultiplier, type Sample } from '../../model';
import { ChartFrame, type ChartLine } from '../ChartFrame';
import { useLab } from '../LabContext';
import { ScaleToggle, SegmentedControl } from '../controls';

export function StrengthChart() {
  const { unitClass, setUnitClass } = useLab();
  // Linear, like every other row. Health, damage and attack speed are three
  // quantities on one axis and they do separate by orders of magnitude, so this
  // is the row the Log toggle earns its place on — but the four rows read as a
  // set, and a set is easier to read when they all start in the same space.
  const [scaleType, setScaleType] = useState<'linear' | 'log'>('linear');

  const lines = useMemo<ChartLine[]>(() => {
    const pick = (s: Sample) => (unitClass === 'boss' ? s.boss : s.normal);
    return [
      { key: 'health', label: 'Health', color: '#6ee7a8', accessor: (s) => pick(s).health },
      { key: 'damage', label: 'Damage', color: '#ff6b81', accessor: (s) => pick(s).damage },
      {
        key: 'speed',
        label: 'Attack speed',
        color: '#a78bfa',
        accessor: (s) => pick(s).speed,
        dashed: true,
      },
    ];
  }, [unitClass]);

  const yDomain = useCallback(
    (samples: Sample[]): [number, number] => {
      let min = Infinity;
      let max = -Infinity;
      for (const sample of samples) {
        const mods = unitClass === 'boss' ? sample.boss : sample.normal;
        min = Math.min(min, mods.health, mods.damage, mods.speed);
        max = Math.max(max, mods.health, mods.damage, mods.speed);
      }
      if (!Number.isFinite(min)) return [1, 2];
      return [min, max];
    },
    [unitClass],
  );

  return (
    <ChartFrame
      index={4}
      title="Biter strength"
      info={
        <>
          <p>
            <b>Normal biters.</b> Health is the extra-lives factor{' '}
            <code>biter_health_factor</code>: 1 below 100% evolution, then the reanimation curve up
            to a 10× cap at 325%, then doubling every +70 evolution points. Damage is{' '}
            <code>1 + (E − 1)</code>, written only once the engine evolution factor reaches 1. There
            is <b>no</b> attack-speed modifier — that line is honestly flat at 1.0×.
          </p>
          <p>
            <b>Boss biters.</b> They spawn past 70% evolution with <code>20 ×</code> the health
            factor, and past 100% take <code>damage = 1 + 2E</code> and{' '}
            <code>attack speed = 1 + 0.25E</code>.
          </p>
          <p>
            Unit tier is a separate channel: it comes from the raffle weights, not from these
            multipliers, and saturates near 20% big / 80% behemoth.
          </p>
        </>
      }
      lines={lines}
      yDomain={yDomain}
      yScaleType={scaleType}
      formatY={(value) => formatMultiplier(value, value >= 10 ? 0 : 1)}
      yLabel="×"
      showBottomAxis
      controls={
        <>
          <SegmentedControl
            value={unitClass}
            onChange={setUnitClass}
            options={[
              { value: 'normal', label: 'Normal' },
              { value: 'boss', label: 'Boss' },
            ]}
          />
          <ScaleToggle value={scaleType} onChange={setScaleType} />
        </>
      }
    />
  );
}
